<script lang="ts">
  // Filled line over a small series — inline SVG, no chart library. Ported
  // from the edge server's Stats.svelte sparklinePath/sparklineArea.
  //
  // The series arrives gap-filled from the server (see
  // analytics/builders.go's sumIntoBuckets), so a weekend is a point at zero
  // rather than a day that quietly isn't there.
  import { accentClasses, edgeLabels } from './chartAccent';

  const {
    values,
    labels = [],
    accent = 'teal',
    ariaLabel,
    unit = ''
  }: {
    values: number[];
    labels?: string[];
    accent?: string;
    ariaLabel: string;
    unit?: string;
  } = $props();

  const W = 240;
  const H = 60;

  const c = $derived(accentClasses(accent));
  const captions = $derived(edgeLabels(labels));

  // Baseline at zero, not at min: a series that runs 30-34 hours should read
  // as steady, and a min-anchored axis would draw it as a cliff.
  const max = $derived(Math.max(...values, 1));
  const min = $derived(Math.min(...values, 0));
  const spread = $derived(max - min || 1);

  function path(vals: number[]): string {
    if (vals.length === 0) return '';
    if (vals.length === 1) return `M0,${H / 2} L${W},${H / 2}`;
    const stepX = W / (vals.length - 1);
    return vals
      .map((v, i) => `${i === 0 ? 'M' : 'L'}${(i * stepX).toFixed(1)},${(H - ((v - min) / spread) * H).toFixed(1)}`)
      .join(' ');
  }

  const line = $derived(path(values));
  const area = $derived(line ? `${line} L${W},${H} L0,${H} Z` : '');
  const peak = $derived(Math.max(...values, 0));
</script>

{#if values.length > 0}
  <div>
    <svg
      viewBox="0 0 {W} {H}"
      preserveAspectRatio="none"
      class="h-14 w-full"
      role="img"
      aria-label="{ariaLabel}: {values.length} points, peak {peak}{unit}"
    >
      <path d={area} class={c.area} />
      <path d={line} fill="none" class={c.stroke} stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
    </svg>
    {#if captions.length > 0}
      <div class="mt-1 flex justify-between text-[10.5px] text-dark-gray">
        {#each captions as caption, i (i)}<span>{caption}</span>{/each}
      </div>
    {/if}
  </div>
{/if}
